import * as React from "react";
import { format } from "date-fns";
import { useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/styles";

import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { Divider, Drawer, Typography } from "@material-ui/core";

import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ErrorIcon from "@material-ui/icons/Error";
import SyncIcon from "@material-ui/icons/Sync";

import { Button, showNotification } from "jazasoft";

import Tabs from "../../../components/tab/Tabs";
import Tab from "../../../components/tab/SubTab";
import { useDeepMemo, useMutation } from "../../../hooks";
import { isEmpty, syntaxHighlight } from "../../../utils/helpers";
import MuiTable from "../../../components/MuiTable";
import { colorLightGreen, colorSecondary, colorTomatoRed } from "../../../utils/colors";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { getCutOrderFlowInfoList } from "../../../helpers/orderHelper";

const StatusField = (value, shortValue, row) => {
  const status = value;
  return (
    <span
      style={{
        backgroundColor: ["Completed", "Synced"].includes(status)
          ? colorLightGreen.dark
          : ["Sync Error"].includes(status)
            ? colorTomatoRed.dark
            : colorSecondary.dark,
        color: "white",
        padding: "4px 8px",
        borderRadius: 4
      }}>
      {status}
    </span>
  );
};

const useStyles = makeStyles(theme => ({
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    // width: 900,
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const IntegrationDrawer = props => {
  const classes = useStyles();
  const { open, onClose, order, orderFlow, preCuttingDeptName, layList = [], layOpList = [], roles, hasAccess, onRefresh } = props;

  const [tabIdx, setTabIdx] = React.useState(0);
  const [dialogState, setDialogState] = React.useState({ active: false, error: null });

  const dispatch = useDispatch();
  const [mutate, { loading: busy }] = useMutation();

  const appList = useDeepMemo(() => {
    let apps = layOpList?.filter(op => isEmpty(op.externalApp?.unitId) || op.externalApp?.unitId === orderFlow?.unitId).map(e => e.externalApp) || [];

    const hasCPL = orderFlow?.orderConfig?.setting?.others?.includes("CPL");
    const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);
    const hasPreCutting = flowInfoList.some(e => e.departments?.includes(preCuttingDeptName));

    if (hasCPL && hasPreCutting && apps.some(e => e.appId === "WFX")) {
      const idx = apps.findIndex(e => e.appId === "WFX");
      apps.splice(idx, 0, { appId: "WFX_CPL", name: "WFX (Pre-Cutting)" });
    }

    return apps;
  }, [layOpList, order, orderFlow, preCuttingDeptName]);

  const rows = useDeepMemo(() => {
    if (isEmpty(layList)) return [];

    const app = appList[tabIdx];
    const appId = app?.appId;

    return layList
      .sort((a, b) => a.layNo - b.layNo)
      .map(lay => {
        const externalIds = !isEmpty(lay.externalIds) ? JSON.parse(lay.externalIds) : [];
        const externalId = externalIds.find(e => e.appId === appId);
        return {
          layNo: lay.layNo,
          status: lay.state,
          syncStatus:
            externalId?.syncedVersion > 0 && isEmpty(externalId.errorMessage)
              ? "Synced"
              : !isEmpty(externalId?.errorMessage)
                ? "Sync Error"
                : "Not Synced",
          lastSyncAt: !isEmpty(externalId?.lastSyncAt) ? format(externalId.lastSyncAt, "MMM dd, yy hh:mm a") : "-",
          errorMessage: externalId?.errorMessage,
          externalId: externalId?.externalId,
          error: externalId?.error
        };
      });
  }, [layList, layOpList, appList, tabIdx]);

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "layNo", title: "Lay No" },
      { dataKey: "externalId", title: "External Id" },
      { dataKey: "lastSyncAt", title: "Last Sync At" },
      { dataKey: "errorMessage", title: "Error Message" },
      { dataKey: "status", title: "Status", render: StatusField },
      { dataKey: "syncStatus", title: "Sync Status", render: StatusField }
    ];
    return cols;
  }, []);

  const onInlineActionClick = (_, action, row) => {
    if (isEmpty(row.error)) {
      dispatch(showNotification("No error details available"));
      return;
    } else {
      setDialogState({ active: true, error: row.error });
    }
  };

  const onSyncClick = () => {
    const options = {
      url: "lays/sync",
      method: "PATCH",
      params: { cutOrderId: orderFlow?.id, appId: "WFX" }
    };
    mutate(options, {
      onSuccess: data => {
        if (!isEmpty(data.errorMessage)) {
          dispatch(showNotification(data.errorMessage, { type: "danger" }));
        } else {
          dispatch(showNotification("Lay synced successfully."));
        }
        onRefresh && onRefresh();
        onClose && onClose();
      }
    });
  };

  const error = dialogState.error?.trim();
  const errorData =
    !isEmpty(error) && ((error.startsWith("{") && error.endsWith("}")) || (error.startsWith("[") && error.endsWith("]")))
      ? syntaxHighlight(JSON.stringify(JSON.parse(error), null, 4))
      : "";

  return (
    <>
      <Dialog
        open={dialogState?.active && !isEmpty(errorData)}
        fullWidth={true}
        maxWidth="md"
        onClose={() => setDialogState({ active: false, error: null })}>
        <DialogContent style={{ padding: "2em" }}>
          <span style={{ whiteSpace: "pre", fontSize: 14 }} dangerouslySetInnerHTML={{ __html: errorData }} />
        </DialogContent>
      </Dialog>

      <Drawer anchor="right" open={open} onClose={onClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onClose}>
            <ChevronRightIcon />
          </IconButton>
          <div style={{ display: "flex", flex: 1, flexDirection: "row", justifyContent: "space-between" }}>
            <Typography variant="h5">Integration Status</Typography>
            {hasPrivilege(roles, hasAccess, ResourceId.LAY_SYNC, Scope.UPDATE) && (
              <Button label="Sync" variant="contained" color="primary" disabled={busy} onClick={onSyncClick}>
                <SyncIcon />
              </Button>
            )}
          </div>
        </div>
        <Divider />
        {layOpList.length > 0 && (
          <div style={{ padding: "1em" }}>
            <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
              {appList.map((app, idx) => (
                <Tab key={idx} label={app?.name} />
              ))}
            </Tabs>
          </div>
        )}

        <div style={{ margin: "1em" }}>
          <MuiTable
            columns={columns}
            rows={rows}
            cellOverFlow="wrap"
            cellLength={90}
            inlineActions={[{ name: "error", tooltip: "Error Details", icon: <ErrorIcon />, options: { style: { color: "red" } } }]}
            onInlineActionClick={onInlineActionClick}
          />
        </div>
      </Drawer>
    </>
  );
};

export default IntegrationDrawer;
